// src/components/clients/SavedViewsBar.jsx
import React, { useEffect, useMemo, useState } from "react";

/**
 * SavedViewsBar
 * Persist and recall "views" (filters/sorting/columns/pageSize) per user.
 * - Storage: localStorage under key `hypercrm.clients.views`
 * - Shareable: also reads/writes URL query (?view=...)
 *
 * Props:
 *   current     : { search, statusFilter, tagFilter, sortBy, sortDir, pageSize, columns }
 *   onApply     : (viewState) => void   // apply a saved view to page state
 *   userId      : string | number       // optional, namespacing storage per-user
 *   className   : string
 */
const STORAGE_KEY = "hypercrm.clients.views";

function useViews(userId) {
  const key = userId ? `${STORAGE_KEY}:${userId}` : STORAGE_KEY;

  const load = () => {
    try {
      const raw = localStorage.getItem(key);
      const parsed = raw ? JSON.parse(raw) : {};
      return parsed && typeof parsed === "object" ? parsed : {};
    } catch {
      return {};
    }
  };

  const [views, setViews] = useState(load);

  useEffect(() => {
    try {
      localStorage.setItem(key, JSON.stringify(views));
    } catch {
      /* ignore quota errors */
    }
  }, [key, views]);

  const upsertView = (name, payload) =>
    setViews((prev) => ({
      ...prev,
      [name]: { ...(prev[name] || {}), ...payload, _updatedAt: Date.now() },
    }));

  const deleteView = (name) =>
    setViews((prev) => {
      const clone = { ...prev };
      delete clone[name];
      return clone;
    });

  return { views, upsertView, deleteView };
}

export default function SavedViewsBar({
  current,
  onApply,
  userId,
  className = "",
}) {
  const { views, upsertView, deleteView } = useViews(userId);
  const [newName, setNewName] = useState("");
  const [menuOpen, setMenuOpen] = useState(false);

  // Handle URL query (?view=My%20View)
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const name = params.get("view");
    if (name && views[name]) {
      onApply?.(views[name]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const viewNames = useMemo(
    () => Object.keys(views).sort((a, b) => (a.toLowerCase() > b.toLowerCase() ? 1 : -1)),
    [views]
  );

  const saveCurrent = () => {
    const name = newName.trim();
    if (!name) return;
    upsertView(name, current);
    setNewName("");
  };

  const applyView = (name) => {
    const v = views[name];
    if (!v) return;
    // Reflect in URL
    const params = new URLSearchParams(window.location.search);
    params.set("view", name);
    const next = `${window.location.pathname}?${params.toString()}`;
    window.history.replaceState({}, "", next);
    onApply?.(v);
    setMenuOpen(false);
  };

  const removeView = (name) => {
    deleteView(name);
    // Clean URL if it referenced this view
    const params = new URLSearchParams(window.location.search);
    if (params.get("view") === name) {
      params.delete("view");
      const next = `${window.location.pathname}${params.toString() ? `?${params.toString()}` : ""}`;
      window.history.replaceState({}, "", next);
    }
  };

  return (
    <div className={`flex items-center gap-2 ${className}`}>
      {/* Quick apply chips */}
      <div className="flex flex-wrap gap-2">
        {viewNames.length ? (
          viewNames.map((name) => (
            <button
              key={name}
              onClick={() => applyView(name)}
              className="inline-flex items-center gap-2 rounded-full bg-gray-100 hover:bg-gray-200 text-gray-700 px-3 py-1 text-sm"
              title="Apply view"
            >
              <span className="truncate max-w-[140px]">{name}</span>
              <span
                className="text-gray-400 hover:text-red-500"
                title="Delete view"
                onClick={(e) => {
                  e.stopPropagation();
                  removeView(name);
                }}
              >
                ×
              </span>
            </button>
          ))
        ) : (
          <span className="text-xs text-gray-400">No saved views</span>
        )}
      </div>

      {/* Divider */}
      <div className="mx-2 h-5 w-px bg-gray-200" />

      {/* Save current */}
      <div className="flex items-center gap-2">
        <input
          value={newName}
          onChange={(e) => setNewName(e.target.value)}
          placeholder="Save current as…"
          className="h-9 w-48 rounded-lg border px-3 text-sm"
          onKeyDown={(e) => {
            if (e.key === "Enter") saveCurrent();
          }}
        />
        <button
          onClick={saveCurrent}
          className="h-9 rounded-lg bg-blue-600 px-3 text-sm font-semibold text-white hover:bg-blue-700"
        >
          Save View
        </button>
      </div>

      {/* Overflow menu (optional) */}
      <div className="relative">
        <button
          onClick={() => setMenuOpen((v) => !v)}
          className="h-9 rounded-lg border bg-white px-3 text-sm hover:bg-gray-50"
        >
          Manage
        </button>
        {menuOpen && (
          <div
            className="absolute right-0 z-20 mt-2 w-60 rounded-xl border border-gray-200 bg-white shadow-lg"
            onMouseLeave={() => setMenuOpen(false)}
          >
            <div className="p-2 text-xs text-gray-500">Saved Views</div>
            <ul className="max-h-64 overflow-auto">
              {viewNames.map((name) => (
                <li key={name} className="flex items-center justify-between px-3 py-2 text-sm">
                  <button
                    className="truncate hover:underline"
                    onClick={() => applyView(name)}
                    title="Apply view"
                  >
                    {name}
                  </button>
                  <button
                    className="text-red-600 hover:underline"
                    onClick={() => removeView(name)}
                    title="Delete"
                  >
                    Delete
                  </button>
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
    </div>
  );
}
